/**
 * W28-G #273: hybrid pause-and-resume across app close/reopen.
 *
 * While a voice session is active we persist {conversationId, mode,
 * timestamp} to localStorage. If the app is closed mid-session, the next
 * visit to the voice-coaching surface offers a resume prompt that
 * reconnects to the same LiveKit room (Simurgh restores context via the
 * isResumed metadata — agent side shipped earlier) and carries the
 * conversation mode through. Cleared on clean end (feedback flow) or when
 * the user explicitly ends/dismisses.
 */

export type StoredConversationMode = 'voice' | 'hybrid';

export interface StoredVoiceSession {
  conversationId: string;
  mode: StoredConversationMode;
  /**
   * W28-G #273 review fix: which voice agent the room belongs to
   * ('simurgh' | 'sphinx'). Resume must only be offered on the matching
   * surface — dispatching Sphinx into a simurgh-prefixed room (or vice
   * versa) is a cross-agent room collision. Entries persisted before this
   * field existed are treated as 'simurgh' (the only agent that persisted
   * sessions back then).
   */
  agentType: string;
  /** Epoch ms when the session was started/last persisted. */
  timestamp: number;
}

// TRF (#2340): exported so the app's user-scoped storage registry can drop it
// when the signed-in person changes.
export const VOICE_ACTIVE_SESSION_KEY = 'lmc-voice-active-session';
const ACTIVE_SESSION_KEY = VOICE_ACTIVE_SESSION_KEY;

/**
 * How long a paused session stays resumable. The agent can restore context
 * from Neo4j at any age, but offering a days-old room as "resume" is more
 * confusing than helpful — beyond this window we silently start fresh.
 */
export const RESUME_WINDOW_MS = 24 * 60 * 60 * 1000;

export function persistActiveSession(session: StoredVoiceSession): void {
  try {
    localStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify(session));
  } catch {
    // Private-mode/quota errors are non-fatal; resume just won’t be offered.
  }
}

export function clearActiveSession(): void {
  try {
    localStorage.removeItem(ACTIVE_SESSION_KEY);
  } catch {
    // ignore
  }
}

/**
 * Read a resumable session, validating shape and freshness.
 * Stale or malformed entries are cleared and null is returned.
 */
export function readResumableSession(now: number = Date.now()): StoredVoiceSession | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(ACTIVE_SESSION_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredVoiceSession>;
    const validMode = parsed.mode === 'voice' || parsed.mode === 'hybrid';
    const validId = typeof parsed.conversationId === 'string' && parsed.conversationId.length > 0;
    const validTs = typeof parsed.timestamp === 'number' && Number.isFinite(parsed.timestamp);
    if (!validMode || !validId || !validTs) {
      clearActiveSession();
      return null;
    }
    if (now - (parsed.timestamp as number) > RESUME_WINDOW_MS) {
      clearActiveSession();
      return null;
    }
    // W28-G #273 review fix: backward compat — entries persisted before
    // agentType existed belong to simurgh.
    const agentType =
      typeof parsed.agentType === 'string' && parsed.agentType.length > 0
        ? parsed.agentType
        : 'simurgh';
    return { ...(parsed as StoredVoiceSession), agentType };
  } catch {
    clearActiveSession();
    return null;
  }
}
